import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { getSocket } from '../../socket';

const preferenceStorageKey = 'tm-agenda-remote-display-preference';
const legacyStorageKey = (meetingId) => `tm-agenda-remote-display:${meetingId}`;
const SOCKET_ACK_TIMEOUT_MS = 8000;

const readPreference = (meetingId) => {
  try {
    const savedPreference = window.localStorage.getItem(preferenceStorageKey);
    if (savedPreference) {
      const parsed = JSON.parse(savedPreference);
      return {
        registered: parsed?.registered === true,
        name: String(parsed?.name || ''),
        migrate: false,
      };
    }

    const legacyRegistered = Boolean(
      JSON.parse(window.sessionStorage.getItem(legacyStorageKey(meetingId)) || 'null')?.registered
    );
    return {
      registered: legacyRegistered,
      name: window.localStorage.getItem(legacyStorageKey(meetingId)) || '',
      migrate: legacyRegistered,
    };
  } catch {
    return { registered: false, name: '', migrate: false };
  }
};

const emitWithAcknowledgement = (socket, event, payload, callback) => {
  if (!socket.connected) {
    callback(new Error('The meeting server is disconnected. Wait for it to reconnect, then try again.'));
    return;
  }
  socket.timeout(SOCKET_ACK_TIMEOUT_MS).emit(event, payload, callback);
};

export function useRemoteDisplayRegistration(meetingId) {
  const [savedRegistration] = useState(() => readPreference(meetingId));
  const [name, setName] = useState(savedRegistration?.name || '');
  const [registered, setRegistered] = useState(Boolean(savedRegistration?.registered));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const registrationRef = useRef(registered);
  const nameRef = useRef(name);

  useEffect(() => {
    registrationRef.current = registered;
    nameRef.current = name;
  }, [name, registered]);

  useEffect(() => {
    if (!savedRegistration.migrate) return;
    try {
      window.localStorage.setItem(preferenceStorageKey, JSON.stringify({
        registered: true,
        name: savedRegistration.name || '',
      }));
    } catch (storageError) {
      setError(storageError.message || 'Unable to save this display preference.');
    }
  }, [savedRegistration]);

  useEffect(() => {
    const socket = getSocket();
    const register = () => {
      if (!registrationRef.current) return;
      emitWithAcknowledgement(socket, 'meeting:remote-display-register', {
        meetingId,
        name: nameRef.current,
      }, (timeoutError, response) => {
        if (timeoutError || !response?.ok) {
          setError(timeoutError
            ? 'No response from the meeting server. Check that it is running the latest version, then try again.'
            : response?.error || 'Unable to register this screen.');
          return;
        }
        setError('');
      });
    };
    const handleDisconnect = () => {
      if (registrationRef.current) setError('Connection lost. Reconnecting this display…');
    };
    const handlePreferenceChanged = (event) => {
      if (event.key !== preferenceStorageKey || !event.newValue) return;
      let preference;
      try {
        preference = JSON.parse(event.newValue);
      } catch {
        setError('The saved remote display preference could not be read.');
        return;
      }
      const shouldRegister = preference?.registered === true;
      const nextName = String(preference?.name || '');
      registrationRef.current = shouldRegister;
      nameRef.current = nextName;
      setRegistered(shouldRegister);
      setName(nextName);
      if (shouldRegister) {
        registerWithName(nextName);
      } else if (socket.connected) {
        socket.emit('meeting:remote-display-unregister', { meetingId });
      }
    };
    const registerWithName = (displayName) => {
      if (!socket.connected) {
        setError('The meeting server is disconnected. Wait for it to reconnect, then try again.');
        return;
      }
      emitWithAcknowledgement(socket, 'meeting:remote-display-register', {
        meetingId,
        name: displayName,
      }, (timeoutError, response) => {
        if (timeoutError || !response?.ok) {
          setError(timeoutError
            ? 'No response from the meeting server. Check that it is running the latest version, then try again.'
            : response?.error || 'Unable to register this screen.');
          return;
        }
        setError('');
      });
    };

    socket.on('connect', register);
    socket.on('disconnect', handleDisconnect);
    window.addEventListener('storage', handlePreferenceChanged);
    register();
    return () => {
      socket.off('connect', register);
      socket.off('disconnect', handleDisconnect);
      window.removeEventListener('storage', handlePreferenceChanged);
      if (registrationRef.current && socket.connected) {
        socket.emit('meeting:remote-display-unregister', { meetingId });
      }
    };
  }, [meetingId]);

  const updateStoredRegistration = (isRegistered, nextName) => {
    registrationRef.current = isRegistered;
    nameRef.current = nextName;
    setRegistered(isRegistered);
    setName(nextName);
    try {
      window.localStorage.setItem(preferenceStorageKey, JSON.stringify({
        registered: isRegistered,
        name: nextName,
      }));
      window.sessionStorage.removeItem(legacyStorageKey(meetingId));
    } catch (storageError) {
      setError(storageError.message || 'Unable to save this display preference.');
    }
  };

  const registerScreen = (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    const socket = getSocket();
    const displayName = name.trim().slice(0, 60);
    emitWithAcknowledgement(socket, 'meeting:remote-display-register', { meetingId, name: displayName }, (timeoutError, result) => {
      setBusy(false);
      if (timeoutError) {
        setError(timeoutError.message.includes('disconnected')
          ? timeoutError.message
          : 'No response from the meeting server. Check that it is running the latest version, then try again.');
        return;
      }
      if (!result?.ok) {
        setError(result?.error || 'Unable to register this screen.');
        return;
      }
      updateStoredRegistration(true, displayName);
    });
  };

  const unregisterScreen = () => {
    setBusy(true);
    const socket = getSocket();
    emitWithAcknowledgement(socket, 'meeting:remote-display-unregister', { meetingId }, (timeoutError, result) => {
      setBusy(false);
      if (timeoutError) {
        setError(timeoutError.message.includes('disconnected')
          ? timeoutError.message
          : 'No response from the meeting server. Check your connection, then try again.');
        return;
      }
      if (!result?.ok) {
        setError(result?.error || 'Unable to stop sharing this screen.');
        return;
      }
      updateStoredRegistration(false, name);
      setError('');
    });
  };

  return {
    name,
    setName,
    registered,
    busy,
    error,
    registerScreen,
    unregisterScreen,
  };
}

export default function RemoteDisplayRegistration({
  name,
  setName,
  registered,
  busy,
  error,
  registerScreen,
  unregisterScreen,
}) {
  return (
    <Card className="remote-display-registration mb-3">
      <Card.Body>
        {registered ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <strong>{name.trim() || 'This screen'} is registered as a remote display</strong>
              <div className="small remote-display-muted">The Timer Manager can send agenda items to this screen.</div>
            </div>
            <Button type="button" variant="outline-secondary" size="sm" disabled={busy} onClick={unregisterScreen}>
              {busy ? 'Unregistering…' : 'Unregister this screen'}
            </Button>
          </div>
        ) : (
          <Form onSubmit={registerScreen}>
            <div className="d-flex flex-wrap align-items-end gap-2">
              <Form.Group className="flex-grow-1">
                <Form.Label className="mb-1">Use this screen as a remote display</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={60}
                  placeholder="Display name (optional)"
                  aria-label="Remote display name (optional)"
                />
              </Form.Group>
              <Button type="submit" disabled={busy}>
                {busy ? 'Registering…' : 'Register this screen'}
              </Button>
            </div>
            <div className="small remote-display-muted mt-1">This browser will register automatically whenever you open a meeting, until you unregister it. No sign-in required.</div>
          </Form>
        )}
        {error && <Alert variant="warning" className="mb-0 mt-2">{error}</Alert>}
      </Card.Body>
    </Card>
  );
}
